import {FC, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {FilteringTag, SearchResult} from 'jbc-front/components/SearchForm'
import {yupResolver} from '@hookform/resolvers/yup'
import {MainFormControls} from 'components/SearchForm/MainFormControls'
import {SearchTextBox} from 'components/SearchForm/SearchTextBox'
import {SortingFields} from 'components/SearchForm/SortingFields'
import {useDisplayEmployeeLimit} from 'hooks/useDisplayEmployeeLimit'
import {useDebouncedSubmit} from 'hooks/useDebouncedSubmit'
import {AdditionalSearchField} from './AdditionalSearchField'
import {Schema, schema, sortTypes, EmploymentStatus, employmentStatuses} from './schema'
import styles from './SearchForm.scss'

const IGNORE_AUTO_SUBMIT_FIELDS = ['status', 'limit', 'sortType']

const limits = ['10', '20', '50', '100', '200'].map((ele) => ({value: ele, label: `${ele}件を表示`}))

interface SearchFormProps {
  values: Schema
  totalCount: number | undefined
  isLoading: boolean
  onSubmit: (data: Schema) => void
  onClear: () => void
  hasConditions: (data: Schema) => boolean
  showJoinedStatusField?: boolean
}

export const SearchForm: FC<SearchFormProps> = ({
  values,
  totalCount,
  isLoading,
  onSubmit,
  onClear,
  hasConditions,
  showJoinedStatusField = true,
}) => {
  const [isExpand, setIsExpand] = useState(false)
  const [limit, setLimit] = useDisplayEmployeeLimit()
  const methods = useForm<Schema>({
    values,
    resolver: yupResolver(schema),
  })
  const {handleSubmit, register, watch, setValue, subscribe} = methods

  const formValues = watch()
  const {status, page} = formValues

  useDebouncedSubmit(subscribe, () => handleSubmit(onSubmit)(), {
    ignoreAutoSubmitFields: IGNORE_AUTO_SUBMIT_FIELDS,
  })

  return (
    <FormProvider {...methods}>
      <div className={styles.searchForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.mainSearchFields}>
            <EmploymentStatusField
              value={status}
              onChange={(value) => {
                if (status !== value) {
                  setValue('status', value)
                  handleSubmit(onSubmit)()
                }
              }}
            />
            <MainFormControls
              isExpand={isExpand}
              hasConditions={hasConditions(formValues)}
              onClear={onClear}
              onToggleClick={() => setIsExpand((prev) => !prev)}
            >
              <SearchTextBox
                {...register('q')}
                style={{width: 230}}
                placeholder="スタッフコード、氏名、メールアドレス"
              />
            </MainFormControls>
          </div>
          {isExpand && <AdditionalSearchField showJoinedStatusField={showJoinedStatusField} />}
          <div className={styles.result}>
            <div className={styles.resultButtonContainer} />
            <SearchResult
              className={styles.resultCenter}
              total={Number(totalCount)}
              page={Number(page)}
              limit={Number(limit)}
              isLoading={isLoading}
              failureMessage="従業員が見つかりませんでした"
            />
            <SortingFields
              limits={limits}
              onChangeLimit={(newValue) => {
                setLimit(newValue)
                handleSubmit(onSubmit)()
              }}
              sortTypes={sortTypes}
              onChangeSortType={() => handleSubmit(onSubmit)()}
            />
          </div>
        </form>
      </div>
    </FormProvider>
  )
}

interface EmploymentStatusFieldProps {
  value: EmploymentStatus
  onChange: (value: EmploymentStatus) => void
}

const EmploymentStatusField: FC<EmploymentStatusFieldProps> = ({value, onChange}) => {
  return (
    <div className={styles.employmentStatusField}>
      {employmentStatuses.map((item) => (
        <FilteringTag key={item.value} selected={item.value === value} onClick={() => onChange(item.value)}>
          <div className={styles.employmentStatus}>{item.label}</div>
        </FilteringTag>
      ))}
    </div>
  )
}
